import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCollectionsQuery, useDeleteCollection } from "../api/queries";
import { CollectionsTour, useCollectionsTour } from "../tour";
import { CollectionsFilter } from "../ui/CollectionsFilter";
import { CollectionsTable } from "../ui/CollectionsTable";
import { CollectionsToolbar } from "../ui/CollectionsToolbar";

export function CollectionsPage() {
  const nav = useNavigate({ from: "/app/catalog/collections" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/catalog/collections",
  });
  const list = useCollectionsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const canManage = useActionPermission(["catalog:create"]);
  const deleteCollection = useDeleteCollection();
  const { run, handleJoyrideCallback, startTour } = useCollectionsTour();

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "ລຶບ Collection",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ Collection ນີ້?",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) {
      toast.promise(deleteCollection.run(id), {
        loading: "ກຳລັງລຶບ...",
        success: "ລຶບ Collection ສຳເລັດ",
        error: "ລຶບ Collection ລົ້ມເຫຼວ",
      });
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄໍເລັກຊັນ" },
          ]}
        />
      </Header>
      <Main>
        <CollectionsToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/catalog/collections/new" })}
          onStartTour={startTour}
        />
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <CollectionsFilter />
          <CollectionsTable
            canManage={!!canManage}
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(row) =>
              nav({
                to: "/app/catalog/collections/$id/edit",
                params: { id: row.id },
              })
            }
            onDelete={handleDelete}
          />
        </div>
        <CollectionsTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
